import { type BackendSource, readBackendSource } from "./storage";

export const SHARED_DEV_BACKEND_ORIGIN = "https://coach-tools.dev.pga.com";
export const LOCAL_BACKEND_GRAPHQL_PATH = "/local-backoffice/graphql";
export const LOCAL_BACKEND_REST_PREFIX = "/local-backend";

export function isBackendSourceToggleAvailable(): boolean {
  if (import.meta.env.MODE.startsWith("portal")) {
    return false;
  }

  return import.meta.env.DEV || import.meta.env.MODE === "cloud-preview";
}

function normalizeOrigin(origin: string): string {
  return origin.endsWith("/") ? origin.slice(0, -1) : origin;
}

export function getLocalBackendOrigin(): string {
  return normalizeOrigin(import.meta.env.VITE_LOCAL_BACKEND_ORIGIN ?? "http://localhost:3000");
}

function inferBackendSourceFromEnv(): BackendSource {
  const graphqlUri = import.meta.env.VITE_GRAPHQL_URI ?? "";

  if (graphqlUri.includes("localhost") || graphqlUri.includes("127.0.0.1")) {
    return "localhost";
  }

  if (graphqlUri.startsWith("/backoffice") || graphqlUri.includes("coach-tools")) {
    return "shared-dev";
  }

  return "shared-dev";
}

export function getActiveBackendSource(): BackendSource {
  if (!isBackendSourceToggleAvailable()) {
    return inferBackendSourceFromEnv();
  }

  return readBackendSource() ?? inferBackendSourceFromEnv();
}

export function getBackendSourceLabel(source: BackendSource): string {
  if (source === "shared-dev") {
    return "dev.pga.com";
  }

  try {
    return new URL(getLocalBackendOrigin()).host;
  } catch {
    return "localhost:3000";
  }
}

export function getGraphqlUri(): string {
  if (!isBackendSourceToggleAvailable()) {
    return import.meta.env.VITE_GRAPHQL_URI;
  }

  const source = getActiveBackendSource();

  if (source === "localhost") {
    return LOCAL_BACKEND_GRAPHQL_PATH;
  }

  const envGraphql = import.meta.env.VITE_GRAPHQL_URI ?? "";
  if (envGraphql.startsWith("/")) {
    return envGraphql;
  }

  return `${SHARED_DEV_BACKEND_ORIGIN}/backoffice/graphql`;
}

export function getBackendUri(): string {
  if (!isBackendSourceToggleAvailable()) {
    return import.meta.env.VITE_BACKEND_URI;
  }

  const source = getActiveBackendSource();
  return source === "localhost" ? LOCAL_BACKEND_REST_PREFIX : SHARED_DEV_BACKEND_ORIGIN;
}

/** Absolute backend origin for browser navigation (`window.open`, links). Use `getBackendUri()` for proxied REST `fetch`. */
export function getBackendNavigationOrigin(): string {
  if (!isBackendSourceToggleAvailable()) {
    return normalizeOrigin(import.meta.env.VITE_BACKEND_URI ?? SHARED_DEV_BACKEND_ORIGIN);
  }

  const source = getActiveBackendSource();
  return source === "localhost" ? getLocalBackendOrigin() : SHARED_DEV_BACKEND_ORIGIN;
}
